// /admin/people — Admin only. Manage people. F-001 self-edit triggers
// the Principal-refresh probe before the table refetches.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { agreementsApi, peopleApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { useUrlState } from '@/utils/useUrlState';
import { usePrincipalContext } from '@/auth/PrincipalContext';
import { useDirtyFormReloadGuard } from '@/utils/useDirtyFormReloadGuard';
import {
  EmptyState,
  ErrorBanner,
  Pagination,
  Skeleton,
} from '@/components/primitives';
import { Modal } from '@/components/ModalHost';
import { useToast } from '@/components/ToastHost';
import { mapApiErrorToFormErrors } from '@/utils/formErrors';
import { ApiError, getOwnedAgreementIds } from '@/types/errors';
import type { Person, Role } from '@/types/domain';
import { formatRole } from '@/utils/format';
import { formatCurrencyString } from '@/utils/format';

const ALL_ROLES: Role[] = ['admin', 'practice_lead', 'member'];

export function PeopleAdminPage() {
  const url = useUrlState();
  const h1Ref = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => h1Ref.current?.focus(), []);

  const roleFilter = url.getAll('role') as Role[];
  const cursor = url.get('cursor');

  const queryArgs = useMemo(
    () => ({
      role: roleFilter.length > 0 ? roleFilter : undefined,
      cursor: cursor ?? undefined,
    }),
    [roleFilter, cursor],
  );

  const list = useQuery({
    queryKey: qk.people(queryArgs),
    queryFn: () => peopleApi.list(queryArgs),
  });

  const [editorOpen, setEditorOpen] = useState<{
    mode: 'create' | 'edit';
    person?: Person;
  } | null>(null);

  return (
    <div>
      <header className="page-header">
        <h1 ref={h1Ref} tabIndex={-1}>
          People
        </h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setEditorOpen({ mode: 'create' })}
        >
          Add person
        </button>
      </header>

      <div className="filter-bar" role="region" aria-label="Filters">
        <fieldset className="filter-group">
          <legend>Role</legend>
          {ALL_ROLES.map((r) => (
            <label key={r} style={{ marginRight: 8, fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={roleFilter.includes(r)}
                onChange={() => {
                  const set = new Set(roleFilter);
                  if (set.has(r)) set.delete(r);
                  else set.add(r);
                  const arr = Array.from(set);
                  url.setFilterOrSort({ role: arr.length ? arr : null });
                }}
              />{' '}
              {formatRole(r)}
            </label>
          ))}
        </fieldset>
      </div>

      {list.isPending ? (
        <Skeleton rows={5} height="32px" />
      ) : list.isError ? (
        <ErrorBanner
          message="Couldn't load people."
          requestId={list.error instanceof ApiError ? list.error.requestId : undefined}
          onRetry={() => void list.refetch()}
        />
      ) : (list.data?.data.length ?? 0) === 0 ? (
        roleFilter.length > 0 ? (
          <EmptyState
            message="No people match this filter."
            action={
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => url.clearAll()}
              >
                Clear filter
              </button>
            }
          />
        ) : (
          <EmptyState message="No people in the system." />
        )
      ) : (
        <>
          <table className="data-table">
            <caption className="visually-hidden">People</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Cost rate</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data!.data.map((p) => (
                <tr key={p.id}>
                  <th scope="row">{p.name}</th>
                  <td>{p.email}</td>
                  <td>{formatRole(p.role)}</td>
                  <td>{formatCurrencyString(p.cost_rate)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => setEditorOpen({ mode: 'edit', person: p })}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            nextCursor={list.data!.next_cursor ?? null}
            loading={list.isFetching}
            onNext={() => {
              if (list.data?.next_cursor) url.setCursor(list.data.next_cursor);
            }}
          />
        </>
      )}

      {editorOpen ? (
        <PersonEditor
          mode={editorOpen.mode}
          person={editorOpen.person ?? null}
          onClose={() => setEditorOpen(null)}
        />
      ) : null}
    </div>
  );
}

interface EditorProps {
  mode: 'create' | 'edit';
  person: Person | null;
  onClose: () => void;
}

interface PersonForm {
  name: string;
  email: string;
  cost_rate: string;
  role: Role;
}

function PersonEditor({ mode, person, onClose }: EditorProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const { principal, onSelfPatched } = usePrincipalContext();

  const defaults: PersonForm = useMemo(
    () =>
      person
        ? {
            name: person.name,
            email: person.email,
            cost_rate: person.cost_rate ?? '',
            role: person.role,
          }
        : { name: '', email: '', cost_rate: '', role: 'member' },
    [person],
  );

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, dirtyFields, isDirty },
  } = useForm<PersonForm>({ defaultValues: defaults });

  // Advisory ownership probe (frontend-design §3 PeopleAdminTable):
  // when editing an existing Person, scan non-Archived agreements for
  // any owned by this Person. Only fires when (a) we're in edit mode,
  // (b) the Person currently has a role we can demote from, and
  // (c) the editor is open. Server enforcement (G-14) is the
  // authoritative gate; this probe powers the client-side advisory
  // tooltip on the Member radio. If the probe itself errors, we fall
  // open (no tooltip) — the server will still reject with the
  // `person_owns_non_archived_agreements` blocking modal below.
  const ownershipProbe = useQuery({
    queryKey: ['people', person?.id ?? 'none', 'owned-non-archived'] as const,
    queryFn: async () => {
      if (!person) return [] as string[];
      // Fetch active+draft+completed agreements (any non-archived);
      // server returns all to Admins. We page in until either we find
      // a match or the cursor is exhausted; max 5 pages of 100 to bound
      // worst-case latency at the V1 scale (20-50 users, low hundreds
      // of agreements). The probe is advisory; an inconclusive empty
      // result simply leaves the radio enabled and the server-side
      // 422 surfaces if the demote-of-owner condition holds.
      const owned: string[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < 5; page++) {
        const res = await agreementsApi.list({
          status: ['draft', 'active', 'completed'],
          limit: 100,
          ...(cursor ? { cursor } : {}),
        });
        for (const ag of res.data) {
          if (ag.owner_person_id === person.id) owned.push(ag.id);
        }
        if (!res.next_cursor) break;
        cursor = res.next_cursor;
      }
      return owned;
    },
    // Only edit-mode + a Person with a demotable starting role need
    // the probe. Member-as-starting-role can still be edited (e.g.,
    // name fix), but cannot be demoted further.
    enabled: mode === 'edit' && !!person && person.role !== 'member',
    staleTime: 30_000,
  });

  // Browser reload-warning safety net per G-FE-1.
  useDirtyFormReloadGuard(isDirty);

  const ownedAgreementCount = ownershipProbe.data?.length ?? 0;
  const isSelfEdit =
    mode === 'edit' &&
    !!person &&
    !!principal &&
    person.id === principal.person_id;
  // Self-demote guard: principal is currently an Admin editing their
  // own row. Demoting to non-Admin is the foot-gun the design contracts
  // disabled-with-tooltip (RC-1 server-side enforcement remains the
  // authoritative gate when the condition holds — i.e., they are the
  // last Admin).
  const selfDemoteGuard =
    isSelfEdit && principal!.role === 'admin' && person!.role === 'admin';
  // Demote-of-owner guard: a Person who currently owns non-Archived
  // agreements cannot be demoted to Member without first transferring
  // ownership (G-14 server-side enforcement remains the authoritative
  // gate).
  const ownsAgreements = ownedAgreementCount > 0;

  // Per frontend-design §3 PeopleAdminTable Disabled state, only the
  // Member radio is gated client-side: (a) self-demote-to-Member when
  // editing one's own Admin row; (b) demote-of-owner-to-Member when
  // the Person currently owns non-Archived agreements. Demoting Admin
  // → PL is permitted client-side in both branches; RC-1 remains the
  // authoritative server-side gate (server returns
  // `last_admin_required` if it would leave 0 Admins, which surfaces
  // through the existing `blockingLastAdmin` modal below).
  const memberRadioDisabledReason: string | null = selfDemoteGuard
    ? 'You cannot demote yourself; ask another Admin.'
    : ownsAgreements
      ? `This Person owns ${ownedAgreementCount} agreement(s). Transfer ownership first.`
      : null;

  const [blockingOwnerships, setBlockingOwnerships] = useState<string[] | null>(null);
  const [blockingLastAdmin, setBlockingLastAdmin] = useState(false);

  const createM = useMutation({
    mutationFn: (body: PersonForm) =>
      peopleApi.create({
        name: body.name.trim(),
        email: body.email.trim().toLowerCase(),
        cost_rate: body.cost_rate ? body.cost_rate : null,
        role: body.role,
      }),
  });
  const patchM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<PersonForm> }) =>
      peopleApi.update(id, {
        ...(body.name !== undefined ? { name: body.name.trim() } : {}),
        ...(body.email !== undefined
          ? { email: body.email.trim().toLowerCase() }
          : {}),
        ...(body.cost_rate !== undefined
          ? { cost_rate: body.cost_rate ? body.cost_rate : null }
          : {}),
        ...(body.role !== undefined ? { role: body.role } : {}),
      }),
  });

  const KNOWN: string[] = ['name', 'email', 'cost_rate', 'role'];

  const onSubmit: SubmitHandler<PersonForm> = async (values) => {
    setBlockingOwnerships(null);
    setBlockingLastAdmin(false);
    // Defensive — the radio is disabled-with-tooltip in both guard
    // cases above, so the user cannot reach this branch through the
    // form, but a programmatic submit (e.g., devtools) would still hit
    // the server's authoritative RC-1 / G-14 rejection. We short-
    // circuit here to avoid an unnecessary network round-trip and
    // surface the same blocking-modal UX the server would.
    if (
      values.role === 'member' &&
      mode === 'edit' &&
      person &&
      ownsAgreements
    ) {
      setBlockingOwnerships(ownershipProbe.data ?? []);
      return;
    }
    try {
      if (mode === 'edit' && person) {
        const body: Partial<PersonForm> = {};
        if (dirtyFields.name) body.name = values.name;
        if (dirtyFields.email) body.email = values.email;
        if (dirtyFields.cost_rate) body.cost_rate = values.cost_rate;
        if (dirtyFields.role) body.role = values.role;
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        await patchM.mutateAsync({ id: person.id, body });
        // F-001 link: self-edit triggers Principal-refresh BEFORE
        // refetch + toast.
        if (principal && person.id === principal.person_id) {
          await onSelfPatched();
        }
        toast.show({ severity: 'success', message: 'Role updated.' });
      } else {
        await createM.mutateAsync(values);
        toast.show({ severity: 'success', message: 'Person added.' });
      }
      void qc.invalidateQueries({ queryKey: ['people'] });
      onClose();
    } catch (err) {
      const mapped = mapApiErrorToFormErrors(err, KNOWN);
      for (const [field, message] of Object.entries(mapped.fieldErrors)) {
        setError(field as keyof PersonForm, { type: 'server', message });
      }
      if (mapped.blockingModal === 'last_admin_required') {
        setBlockingLastAdmin(true);
      } else if (
        mapped.blockingModal === 'person_owns_non_archived_agreements' &&
        err instanceof ApiError
      ) {
        setBlockingOwnerships(getOwnedAgreementIds(err));
      } else if (mapped.formMessage) {
        toast.show({ severity: 'error', message: mapped.formMessage });
      }
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={mode === 'create' ? 'Add person' : `Edit ${person?.name ?? 'person'}`}
      role="dialog"
      variant="drawer"
      canClose={() => !isDirty || window.confirm('Discard unsaved changes?')}
    >
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className="form-field">
          <label htmlFor="pe-name">
            Name<span className="form-required">*</span>
          </label>
          <input
            id="pe-name"
            disabled={isSubmitting}
            {...register('name', {
              required: 'Name is required.',
              validate: (v) => v.trim().length > 0 || 'Name is required.',
            })}
          />
          {errors.name ? (
            <span className="form-error">{errors.name.message}</span>
          ) : null}
        </div>
        <div className="form-field">
          <label htmlFor="pe-email">
            Email<span className="form-required">*</span>
          </label>
          <input
            id="pe-email"
            type="email"
            disabled={isSubmitting}
            {...register('email', {
              required: 'Email is required.',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email.',
              },
            })}
          />
          {errors.email ? (
            <span className="form-error">{errors.email.message}</span>
          ) : null}
        </div>
        <div className="form-field">
          <label htmlFor="pe-cost">Cost rate (USD/hour)</label>
          <input
            id="pe-cost"
            inputMode="decimal"
            disabled={isSubmitting}
            {...register('cost_rate', {
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: 'Enter dollars and cents.',
              },
            })}
          />
          {errors.cost_rate ? (
            <span className="form-error">{errors.cost_rate.message}</span>
          ) : null}
        </div>
        <fieldset className="form-field">
          <legend>
            Role<span className="form-required">*</span>
          </legend>
          {ALL_ROLES.map((r) => {
            // Only the Member radio is gated client-side per design.
            const reason: string | null =
              r === 'member' ? memberRadioDisabledReason : null;
            const disabled = isSubmitting || reason !== null;
            return (
              <label
                key={r}
                style={{ marginRight: 16 }}
                title={reason ?? undefined}
              >
                <input
                  type="radio"
                  value={r}
                  disabled={disabled}
                  aria-disabled={disabled ? 'true' : undefined}
                  aria-describedby={
                    reason ? `pe-role-${r}-reason` : undefined
                  }
                  {...register('role', { required: true })}
                />{' '}
                {formatRole(r)}
                {reason ? (
                  <span
                    id={`pe-role-${r}-reason`}
                    className="visually-hidden"
                  >
                    {reason}
                  </span>
                ) : null}
              </label>
            );
          })}
          {memberRadioDisabledReason ? (
            <p
              className="muted"
              role="note"
              style={{ marginTop: 4, fontSize: '0.875rem' }}
            >
              {memberRadioDisabledReason}
            </p>
          ) : null}
        </fieldset>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : mode === 'edit' ? 'Save' : 'Add person'}
          </button>
        </div>
      </form>

      {blockingLastAdmin ? (
        <Modal
          open={true}
          onClose={() => setBlockingLastAdmin(false)}
          title="At least one Admin is required"
          role="alertdialog"
          scrimDismissDisabled
        >
          <p>Promote another person to Admin first, then try again.</p>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setBlockingLastAdmin(false)}
            >
              Close
            </button>
          </div>
        </Modal>
      ) : null}

      {blockingOwnerships ? (
        <Modal
          open={true}
          onClose={() => setBlockingOwnerships(null)}
          title="This person owns non-archived agreements"
          role="alertdialog"
          scrimDismissDisabled
        >
          <p>
            Transfer ownership of {blockingOwnerships.length} agreement(s) before
            demoting this person to Member.
          </p>
          <ul>
            {blockingOwnerships.map((id) => (
              <li key={id}>
                <a href={`/agreements/${id}`}>{id}</a>
              </li>
            ))}
          </ul>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setBlockingOwnerships(null)}
            >
              Close
            </button>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}
